import numeral from "numeral";

import type { Database, DocumentData } from ".";

export const quotasCollectionName = "quotas";

export enum QuotaContext {
    App = "app",
}

export enum QuotaKind {
    RowsUsed = "rows-used",
    ZapsAndWebhook = "zaps",
    Reloads = "reloads",
    Signatures = "signatures",
    DeliverEmail = "deliver-email",
    FileBytesUsed = "file-bytes-used",
    BarcodesScanned = "barcodes-scanned",
}

export const quotaNeedsReset: Record<QuotaKind, boolean> = {
    [QuotaKind.RowsUsed]: false,
    [QuotaKind.ZapsAndWebhook]: true,
    [QuotaKind.Reloads]: true,
    [QuotaKind.Signatures]: true,
    [QuotaKind.DeliverEmail]: true,
    [QuotaKind.FileBytesUsed]: false,
    [QuotaKind.BarcodesScanned]: true,
};

export function isQuotaKind(k: string): k is QuotaKind {
    return Object.keys(quotaNeedsReset).indexOf(k) >= 0;
}

export interface QuotaValues {
    readonly limit?: number;
    readonly current: number;
    readonly lastResetAt?: Date;
}

interface QuotaInfo {
    readonly kind: QuotaKind;
    readonly shortName: string;
    readonly longName?: string;
    readonly subtitle: string | undefined;
    readonly formatFunc?: (item: number) => string;
}

export const quotaInfos: QuotaInfo[] = [
    {
        kind: QuotaKind.RowsUsed,
        shortName: "Rows",
        subtitle: undefined,
    },
    {
        kind: QuotaKind.FileBytesUsed,
        longName: "Images & Files",
        shortName: "Storage",
        subtitle: undefined,
        formatFunc: i => numeral(i).format("0b"),
    },
    {
        kind: QuotaKind.Reloads,
        shortName: "Edits",
        longName: "Sheet Edits",
        subtitle: "(Per month)",
    },
    {
        kind: QuotaKind.ZapsAndWebhook,
        longName: "Zaps & Webhooks",
        shortName: "Webhooks",
        subtitle: "(Per month)",
    },
    {
        kind: QuotaKind.Signatures,
        shortName: "Signatures",
        subtitle: "(Per month)",
    },
    {
        kind: QuotaKind.DeliverEmail,
        longName: "Emails Sent",
        shortName: "Emails",
        subtitle: "(Per month)",
    },
    {
        kind: QuotaKind.BarcodesScanned,
        longName: "Barcodes Scanned",
        shortName: "Barcodes",
        subtitle: "(Per month)",
    },
];

export interface QuotaValuesWithLimit extends QuotaValues {
    readonly limit: number;
}

export type QuotaValuesForKinds = Partial<Record<QuotaKind, QuotaValues>>;

export type AllQuotaValuesWithLimitsForKinds = Record<QuotaKind, QuotaValuesWithLimit>;

export interface QuotaDocument {
    readonly quotas: QuotaValuesForKinds;
    readonly quotaVersions: Partial<Record<QuotaKind, { confirmed: number; next: number }>>;
}

export function makeQuotaDocumentID(context: QuotaContext, id: string): string {
    return `${context}-${id}`;
}

export function quotasFromDocument(db: Database, data: DocumentData): QuotaDocument {
    return (db.convertFromDocument(data) as unknown) as QuotaDocument;
}
